// Destination Country Step
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { loadCountries } from "../../../../../services/common.service";
import { Flag } from "../../../../Flag";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import FlatListDataComponent from "../../../common/FlatListDataComponent";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import { matchesSearchQuery } from "../../../../../helper/searchTextHelper";

type ApiCountry = {
  id?: number;
  CountryCode: string;
  CountryName: string;
  IsActive: number | boolean;
};

type ChooseCountryStepProps = {
  onCountrySelected?: (code: string) => void;
  testID?: string | undefined;
};

export default function ChooseCountryStep({ onCountrySelected, testID }: ChooseCountryStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const storedCountryName = useSessionStore((s: SessionState) => s.selectedDestinationCountryName);
  const [selectedCountry, setSelectedCountry] = useState(
    () => useSessionStore.getState().selectedDestinationCountryName ?? ""
  );
  const [countriesData, setCountriesData] = useState<ApiCountry[]>([]);

  const setSelectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationCountryCode
  );
  const setSelectedDestinationCountryName = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationCountryName
  );

  useEffect(() => {
    setSelectedCountry(storedCountryName ?? "");
  }, [storedCountryName]);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await loadCountries();
        const apiCountries: ApiCountry[] = (result?.data ?? []).filter((c: ApiCountry) => {
          const isActive = typeof c.IsActive === "number" ? c.IsActive === 1 : !!c.IsActive;
          return isActive && !!c.CountryName && !!c.CountryCode;
        });
        setCountriesData(apiCountries);
      } catch (e) {
        console.error("Failed to load countries in ChooseCountryStep", e);
      }
    };
    fetchCountries();
  }, []);

  // Filter countries by search input
  const filteredCountries: ApiCountry[] = useMemo(() => {
    return countriesData.filter((c) =>
      matchesSearchQuery([c.CountryName, c.CountryCode], search),
    );
  }, [countriesData, search]);

  const onSelectCountry = (item: ApiCountry) => {
    setSelectedCountry(item.CountryName);
    setSelectedDestinationCountryName(item.CountryName);
    setSelectedDestinationCountryCode(item.CountryCode);
    setTimeout(() => {
      onCountrySelected?.(item.CountryCode);
    }, 200);
    captureCountryEvent(item.CountryName)
  };

  const captureCountryEvent = (selectedCountry: string) => {
    capturePosthogEvent('Send Flow - Country Capture Step', {
      'data-mg-country': selectedCountry
    });
  }

  return (
    <View style={styles.containerSticky} testID={testID}>
      <FlatListDataComponent
        stickyHeader
        title={t("COUNTRY_STEP_TITLE")}
        subTitle={t("COUNTRY_STEP_SUBTITLE")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredCountries}
        searchTestID="countrySearchInput"
        flatListTestID="countryList"
        renderItem={(item: ApiCountry) => {
          return (
            <TouchableOpacity
              style={styles.countryRow}
              onPress={() => onSelectCountry(item)}
              testID={`countrySelected`}
            >
              <View style={styles.countryRowMain}>
                <View style={styles.countryFlag}>
                  <Flag iso3={item.CountryCode ?? ""} width={24} height={24} />
                </View>
                <Text
                  style={styles.countryName}
                  numberOfLines={2}
                  ellipsizeMode="tail"
                >
                  {item.CountryName}
                </Text>
              </View>
              <View style={styles.radioButton}>
                {selectedCountry === item.CountryName && (
                  <RadioSelectedIcon width={20} height={20} />
                )}
              </View>
            </TouchableOpacity>
          );
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  containerSticky: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    paddingVertical: 16,
    borderBottomWidth: 0,
  },
  countryRowMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    marginRight: 12,
  },
  countryFlag: {
    flexShrink: 0,
  },
  countryName: {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.darkGrey,
    marginLeft: 16,
  },
  radioButton: {
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.radioButtonFilled,
  },
});
